import { FC, useMemo } from 'react'

import IconCheckComponent from '@/assets/icons/components/IconCheck'
import {
  Size,
  ThumbnailType,
  mapSizeToClass
} from '@/components/avatar/constants'
import IconWiggot from '@/components/icon-wiggot'
import { composeClasses } from '@/utils/compose-classes'
import { MdPerson } from 'react-icons/md'

export interface AvatarProps {
  size?: Size
  fullName?: string
  classFullName?: string
  avatarUrl?: string
  avatarIconType?: ThumbnailType
  isVerified?: boolean
  containerClassName?: string
}

const AvatarWithType: FC<{ type: ThumbnailType }> = ({ type }) => {
  if (type === 'profile') {
    return (
      <MdPerson
        data-testid="icon-profile"
        className="text-surface-dark text-[24px]"
      />
    )
  }
  return <IconWiggot variant="black" />
}

export const Avatar: FC<AvatarProps> = ({
  size = 'md',
  fullName = '',
  classFullName,
  avatarUrl,
  avatarIconType,
  isVerified = false,
  containerClassName
}) => {
  const initials = useMemo(() => {
    if (!fullName) return ''
    return fullName
      ?.split(' ')
      ?.map((name) => name?.[0])
      ?.join('')
      ?.slice(0, 2)
      ?.toUpperCase()
  }, [fullName])

  return (
    <div
      className={composeClasses(
        containerClassName,
        'rounded-full bg-surface-gray-20 flex items-center justify-center relative',
        mapSizeToClass?.[size]
      )}
      data-testid="avatar-container"
    >
      {avatarUrl && (
        <img
          src={avatarUrl}
          alt={fullName}
          className="w-full h-full rounded-full top-0 left-0 absolute object-cover"
          data-testid="avatar-image"
        />
      )}
      {!initials ? (
        <AvatarWithType type={avatarIconType ?? 'profile'} />
      ) : (
        <span className={composeClasses('text-body-bold-m', classFullName)}>
          {initials}
        </span>
      )}
      {isVerified && (
        <div className="absolute right-0 bottom-0 bg-white p-[1px] rounded-full flex items-center">
          <IconCheckComponent
            width="1rem"
            height="1rem"
            data-testid="icon-check"
          />
        </div>
      )}
    </div>
  )
}

export default Avatar
